import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'en' ? 'es' : 'en');
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const transparentMode = isHome && !scrolled;

  const navLinks = [
    { path: '/', label: t('nav_home') },
    { path: '/water-purification', label: t('nav_water') },
    { path: '/windows-services', label: t('nav_windows') },
    { path: '/about', label: t('nav_about') },
    { path: '/contact', label: t('nav_contact') },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-white border-b border-slate-100 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src="https://public.readdy.ai/ai/img_res/e0a102db-697a-45ac-b9ed-1cd4040cea79.png"
            alt="ORPM Investment LLC"
            className="h-11 w-auto"
          />
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-semibold tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
                transparentMode
                  ? location.pathname === link.path
                    ? 'text-sky-300'
                    : 'text-white/90 hover:text-sky-300'
                  : location.pathname === link.path
                  ? 'text-sky-600'
                  : 'text-slate-700 hover:text-sky-600'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right side */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Language switcher */}
          <button
            onClick={toggleLang}
            className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
              transparentMode
                ? 'border-white/40 text-white/80 hover:bg-white/10'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
            aria-label="Toggle language"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-global-line text-sm"></i>
            </div>
            {i18n.language === 'en' ? 'ES' : 'EN'}
          </button>
          <a
            href="tel:+15615122820"
            className={`text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer ${
              transparentMode ? 'text-white/90 hover:text-sky-300' : 'text-slate-700 hover:text-sky-600'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-base"></i>
            </div>
            +1 (561) 512-2820
          </a>
          <Link
            to="/contact"
            className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-5 py-2.5 rounded-md whitespace-nowrap transition-colors cursor-pointer"
          >
            {t('nav_quote')}
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden cursor-pointer w-8 h-8 flex items-center justify-center ${
            transparentMode ? 'text-white' : 'text-slate-800'
          }`}
          aria-label="Toggle menu"
        >
          <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-6 py-5 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-semibold py-2 border-b border-slate-50 cursor-pointer ${
                location.pathname === link.path ? 'text-sky-600' : 'text-slate-700'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href="tel:+15615122820"
            className="text-sm text-slate-700 flex items-center gap-2 py-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line"></i>
            </div>
            +1 (561) 512-2820
          </a>
          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className="flex-1 bg-sky-600 text-white text-sm font-semibold px-5 py-3 rounded-md text-center cursor-pointer"
            >
              {t('nav_quote')}
            </Link>
            <button
              onClick={toggleLang}
              className="border border-slate-200 text-slate-600 text-xs font-bold px-3 py-3 rounded-md cursor-pointer whitespace-nowrap"
            >
              {i18n.language === 'en' ? 'ES' : 'EN'}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
